// Amada ⇄ Hulk Form Builder.
//
// One runtime for every Hulk embed on the store. It waits for Hulk to render, writes the
// theme's own facts into named fields, restyles the result into Amada UI, and replaces Hulk's
// success state with the theme's.
//
// Fields are declared per placement in `snippets/amada-hulk-form.liquid` as a JSON object
// (name → value) and matched against the form's inputs by name. Matching Hidden fields are
// optional enhancements for designed emails and attribution; unresolved names are reported to
// the console rather than failing the submission, so the existing Piece details field can still
// carry the complete product summary.
//
// Before the Pro plan, hidden fields were paid and the whole payload had to be squeezed into one
// free Paragraph field found by reading labels. That fallback survives here only as the last
// matching strategy — it is what still fills a field labelled "Piece details" from `piece_details`.
//
// Current Hulk builds (empty `form_data`, no app proxy, or captcha enabled) mount the form in a
// cross-origin iframe. Parent-page writes cannot reach those inputs, which is why piece tokens
// submit empty. When that happens this runtime hides the iframe, draws the Amada fields itself,
// and posts the filled payload to Hulk's formresponse endpoint.

(function () {
  'use strict';

  if (window.AmadaHulk) {
    window.AmadaHulk.boot();
    return;
  }

  var POLL_MS = 200;
  var POLL_MAX = 50;
  var SUBMIT_TIMEOUT_MS = 30000;
  var HULK_FORM_HOST = 'https://formbuilder.hulkapps.com';
  var RESPONSE_LABELS = {
    piece_details: 'Piece details',
    email: 'Email',
    name: 'Name',
  };
  var WRAPPER_SELECTORS =
    '.form-group, [class*="formElement_"], .form-element, .px-form-group, .field-container, .px-field, [class*="FormField"], [class*="form-field"], [class*="formField"], li';

  var ATTRIBUTION_KEY = 'amada:attribution';
  var ATTRIBUTION_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'fbclid',
  ];

  // The order the combined block prints in, and the label each fact carries in it.
  var SUMMARY_ORDER = [
    ['piece_title', ''],
    ['piece_variant', ''],
    ['piece_price', ''],
    ['piece_specs', ''],
    ['piece_sku', 'SKU: '],
    ['piece_url', ''],
    ['piece_image', 'Image: '],
  ];

  function normalize(text) {
    return String(text || '')
      .replace(/\s+/g, ' ')
      .replace(/[*:]+$/, '')
      .trim()
      .toLowerCase();
  }

  // "Piece details", "piece-details" and "piece_details" are the same field as far as matching
  // is concerned: Hulk names an input off the label an admin typed, and that casing is not ours.
  function key(text) {
    return normalize(text)
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  function json(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      /* A malformed payload costs the fields it carried, not the submission. */
      return null;
    }
  }

  function absolute(url, base) {
    if (!url) return '';
    if (url.slice(0, 2) === '//') url = 'https:' + url;
    if (!base) return url;
    try {
      return new URL(url, base).href;
    } catch (error) {
      return url;
    }
  }

  // First touch, per session: a shopper who arrives on a campaign landing page and submits three
  // pages later still carries the campaign that brought them. Reading the address bar at submit
  // time instead would credit every one of those leads to nothing.
  function attribution() {
    var stored = null;
    try {
      stored = JSON.parse(window.sessionStorage.getItem(ATTRIBUTION_KEY) || 'null');
    } catch (error) {
      /* Private browsing: fall through and compute a fresh, unstored value. */
    }
    if (stored) return stored;

    var out = {};
    try {
      var params = new URL(window.location.href).searchParams;
      for (var i = 0; i < ATTRIBUTION_PARAMS.length; i++) {
        var value = params.get(ATTRIBUTION_PARAMS[i]);
        if (value) out[ATTRIBUTION_PARAMS[i]] = value;
      }
    } catch (error) {
      /* ignore an unparseable location */
    }

    var referrer = document.referrer || '';
    if (referrer) {
      try {
        if (new URL(referrer).host !== window.location.host) out.referrer = referrer;
      } catch (error) {
        /* ignore an unparseable referrer */
      }
    }

    out.landing_page = window.location.href.split('#')[0];

    try {
      window.sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(out));
    } catch (error) {
      /* ignore a full or unavailable store */
    }
    return out;
  }

  function declared(root) {
    if (root.amadaFields) return root.amadaFields;
    root.amadaFields = json(root.querySelector('.amada-hulk__fields')) || {};
    return root.amadaFields;
  }

  function variants(root) {
    if (root.amadaVariants) return root.amadaVariants;
    root.amadaVariants = json(root.querySelector('.amada-hulk__variants')) || [];
    return root.amadaVariants;
  }

  // The add-to-cart form's hidden id is what the shopper would actually buy: it is
  // server-rendered on every morph and kept in sync by the variant picker, which the address
  // bar is not — `?variant=` is only written after the section fetch resolves. Climbing from
  // the panel outwards finds this product's form rather than a recommendation's.
  function variantIdFromForm(root) {
    var node = root;
    while (node && node !== document.body) {
      var input = node.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
      if (input && input.value) return input.value;
      node = node.parentElement;
    }
    return null;
  }

  function variantIdFromUrl() {
    try {
      return new URL(window.location.href).searchParams.get('variant');
    } catch (error) {
      return null;
    }
  }

  // Only ever returns a variant off this block's own list, so a stray id from elsewhere on the
  // page cannot pin another product's price to this piece. When nothing resolves, the price and
  // SKU lines are simply dropped — quoting the first variant would name a metal nobody chose.
  function selectedVariant(root) {
    var list = variants(root);
    if (!list.length) return null;

    var id = variantIdFromForm(root) || variantIdFromUrl();
    if (id) {
      for (var i = 0; i < list.length; i++) {
        if (String(list[i].id) === String(id)) return list[i];
      }
    }

    return list.length === 1 ? list[0] : null;
  }

  function variantUrl(base, variant) {
    if (!base) return window.location.href.split('#')[0];
    if (!variant) return base;
    try {
      var canonical = new URL(base);
      canonical.searchParams.set('variant', variant.id);
      return canonical.href;
    } catch (error) {
      return base;
    }
  }

  // One labelled line per fact, for the notification's readability and as insurance: if a token
  // in either email template is mistyped, this single field still carries the whole piece.
  function summary(values) {
    var lines = [];
    for (var i = 0; i < SUMMARY_ORDER.length; i++) {
      var name = SUMMARY_ORDER[i][0];
      var prefix = SUMMARY_ORDER[i][1];
      if (values[name]) lines.push(prefix + values[name]);
    }
    return lines.join('\n');
  }

  // Recomputed on every fill rather than cached: the shopper can change size or metal after the
  // panel has rendered, and what they had selected when they pressed the button is the only
  // reading of "this piece" that is true.
  function fields(root) {
    var values = {};
    var base = declared(root);
    for (var name in base) {
      if (Object.prototype.hasOwnProperty.call(base, name) && base[name] !== '') {
        values[name] = base[name];
      }
    }

    var touch = attribution();
    for (var param in touch) {
      if (Object.prototype.hasOwnProperty.call(touch, param) && !values[param]) {
        values[param] = touch[param];
      }
    }
    values.page_url = window.location.href.split('#')[0];

    var variant = selectedVariant(root);
    if (variant) {
      values.piece_url = variantUrl(values.piece_url, variant);
      if (variant.title && variant.title !== 'Default Title') values.piece_variant = variant.title;
      if (variant.price) values.piece_price = variant.price;
      if (variant.sku) values.piece_sku = variant.sku;
      if (variant.image) values.piece_image = variant.image;
    }

    if (values.piece_image) {
      values.piece_image = absolute(values.piece_image, values.piece_url || window.location.href);
    }

    if (!values.piece_details) {
      var block = summary(values);
      if (block) values.piece_details = block;
    }

    return values;
  }

  function wrapper(node) {
    if (!node) return null;
    return node.closest(WRAPPER_SELECTORS) || node.parentElement;
  }

  function labelled(scope, wanted) {
    var labels = scope.querySelectorAll('label, .control-label, .px-label, legend, span, p, div, td');
    for (var i = 0; i < labels.length; i++) {
      if (key(labels[i].textContent) !== wanted) continue;

      var forId = labels[i].getAttribute('for');
      if (forId) {
        try {
          var byFor = scope.querySelector('#' + CSS.escape(forId));
          if (byFor) return byFor;
        } catch (error) {
          /* ignore an id that is not a valid selector */
        }
      }

      var wrap = wrapper(labels[i]) || labels[i].parentElement;
      var control =
        wrap &&
        wrap.querySelector(
          'textarea, input[type="text"], input[type="hidden"], input:not([type="checkbox"]):not([type="radio"]):not([type="submit"]):not([type="button"])'
        );
      if (control) return control;
    }
    return null;
  }

  // Hulk decides the rendered `name` from what an admin typed into the field label, and how it
  // namespaces that has changed between app versions. Rather than pin one shape, try the exact
  // name, then the bracketed form, then any input whose name, id or data-name reduces to the
  // same key, and only then read labels.
  function findControl(embed, name) {
    if (!/^[A-Za-z0-9_-]+$/.test(name)) return null;

    var selectors = ['[name="' + name + '"]', '[name$="[' + name + ']"]', '[data-name="' + name + '"]'];
    for (var i = 0; i < selectors.length; i++) {
      try {
        var direct = embed.querySelector(selectors[i]);
        if (direct) return direct;
      } catch (error) {
        /* ignore an unsupported selector */
      }
    }

    var wanted = key(name);
    var controls = embed.querySelectorAll('input, textarea, select');
    for (var c = 0; c < controls.length; c++) {
      var control = controls[c];
      if (
        key(control.name) === wanted ||
        key(control.id) === wanted ||
        key(control.getAttribute('data-name')) === wanted
      ) {
        return control;
      }
    }

    return labelled(embed, wanted);
  }

  // Assigning `.value` alone is ignored by any form whose framework tracks its own state, so go
  // through the native setter and announce the change the way a keystroke would.
  function setValue(control, value) {
    var proto = control.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement : window.HTMLInputElement;
    var descriptor = proto && Object.getOwnPropertyDescriptor(proto.prototype, 'value');
    if (descriptor && descriptor.set) {
      descriptor.set.call(control, value);
    } else {
      control.value = value;
    }
    control.setAttribute('value', value);
    if (control.tagName === 'TEXTAREA') control.textContent = value;
    control.dispatchEvent(new Event('input', { bubbles: true }));
    control.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // A Hidden field needs no hiding. Anything else carrying our data is a visible field being
  // used as a carrier, and the shopper should never see the machinery.
  function conceal(control) {
    if (control.type === 'hidden') return;
    var wrap = wrapper(control);
    if (wrap) {
      wrap.classList.add('amada-hulk__concealed');
      wrap.setAttribute('aria-hidden', 'true');
    }
    control.setAttribute('aria-hidden', 'true');
    control.tabIndex = -1;
  }

  function fill(root, embed) {
    var values = fields(root);
    var missing = [];

    if (!embed.amadaControls) embed.amadaControls = {};

    for (var name in values) {
      if (!Object.prototype.hasOwnProperty.call(values, name)) continue;

      var control = embed.amadaControls[name];
      if (!control || !control.isConnected) {
        control = findControl(embed, name);
        if (control) embed.amadaControls[name] = control;
      }

      if (!control) {
        missing.push(name);
        continue;
      }

      setValue(control, values[name]);
      conceal(control);
    }

    return missing;
  }

  function firstMatching(scope, selectors) {
    for (var i = 0; i < selectors.length; i++) {
      var node = scope.querySelector(selectors[i]);
      if (node) return node;
    }
    return null;
  }

  function findByLabel(scope, names) {
    var labels = scope.querySelectorAll('label, .control-label, .px-label, legend, span, p');
    for (var i = 0; i < labels.length; i++) {
      var text = normalize(labels[i].textContent);
      for (var n = 0; n < names.length; n++) {
        if (text === names[n] || text.indexOf(names[n]) === 0) {
          var wrap = wrapper(labels[i]);
          var control = wrap && wrap.querySelector('input, textarea, select');
          if (control) return control;
        }
      }
    }
    return null;
  }

  function attribute(root, name, fallback) {
    var value = root.getAttribute(name);
    return value === null || value === '' ? fallback : value;
  }

  // Hulk's own markup is generic app chrome. Everything below renames it into Amada's classes so
  // one stylesheet dresses every placement, and enforces the two rules Hulk cannot: name and
  // email are required, and on the inline layout they sit on one line with the button.
  function restyle(root, embed) {
    if (embed.dataset.amadaRestyled === 'true') return;
    var form = embed.querySelector('form') || embed;
    form.classList.add('amada-hulk__form');

    var name =
      findByLabel(embed, ['name', 'your name', 'full name']) ||
      firstMatching(embed, [
        'input[autocomplete="name"]',
        'input[autocomplete="given-name"]',
        'input[name*="name" i]:not([type="hidden"])',
        'input[type="text"]:not([type="hidden"])',
      ]);
    var email =
      findByLabel(embed, ['email', 'email address', 'e-mail']) ||
      firstMatching(embed, ['input[type="email"]', 'input[name*="email" i]']);
    var submit = firstMatching(embed, [
      '[type="submit"]',
      'button.btn',
      'button[class*="submit"]',
      'input[type="button"]',
      'button:not([type="button"]):not([type="reset"])',
    ]);

    if (name && name.type !== 'hidden') {
      name.classList.add('amada-hulk__input', 'amada-hulk__input--name');
      name.required = true;
      name.setAttribute('aria-required', 'true');
      var namePlaceholder = root.getAttribute('data-amada-name-placeholder');
      if (namePlaceholder) name.setAttribute('placeholder', namePlaceholder);
      var nameWrap = wrapper(name);
      if (nameWrap) nameWrap.classList.add('amada-hulk__name');
    }

    if (email) {
      email.classList.add('amada-hulk__input');
      email.required = true;
      email.setAttribute('aria-required', 'true');
      var emailPlaceholder = root.getAttribute('data-amada-email-placeholder');
      if (emailPlaceholder) email.setAttribute('placeholder', emailPlaceholder);
      var emailWrap = wrapper(email);
      if (emailWrap) emailWrap.classList.add('amada-hulk__email');
    }

    if (submit) {
      submit.classList.add('amada-hulk__submit');
      var submitLabel = root.getAttribute('data-amada-submit-label');
      if (submitLabel && !submit.querySelector('img, svg')) {
        if (submit.tagName === 'INPUT') submit.value = submitLabel;
        else submit.textContent = submitLabel;
      }
      var submitWrap = submit.closest('.form_submit_div') || submit.parentElement;
      if (submitWrap) submitWrap.classList.add('amada-hulk__submit-wrap');
    }

    if (root.getAttribute('data-amada-layout') === 'inline' && email && submit) {
      var emailBox = wrapper(email);
      var submitBox = submit.closest('.form_submit_div') || submit.parentElement;
      if (emailBox && submitBox && emailBox !== submitBox && !emailBox.closest('.amada-hulk__row')) {
        var pair = document.createElement('div');
        pair.className = 'amada-hulk__row';
        emailBox.parentNode.insertBefore(pair, emailBox);
        pair.appendChild(emailBox);
        pair.appendChild(submitBox);
      }

      // Hulk orders fields as the admin dragged them; the name field belongs above the pair
      // whichever way round that was.
      var row = embed.querySelector('.amada-hulk__row');
      var nameBox = name && name.type !== 'hidden' && wrapper(name);
      if (row && nameBox && row.parentNode) row.parentNode.insertBefore(nameBox, row);
    }

    // Consent is Hulk's to define when the form declares it. The local fallback is only a
    // required storefront gate for this placement; Hulk ignores controls that are not in its
    // formElements configuration, so a recorded consent field should still be created in Hulk.
    var consentLabel = root.getAttribute('data-amada-consent-label');
    var checkboxes = embed.querySelectorAll('input[type="checkbox"]');
    var consent = checkboxes[0] || null;

    if (checkboxes.length) {
      for (var b = 0; b < checkboxes.length; b++) {
        var box = checkboxes[b];
        box.classList.add('amada-hulk__check-control');
        var boxWrap = box.closest('label') || wrapper(box) || box.parentElement;
        if (boxWrap) {
          boxWrap.classList.add('amada-hulk__consent');
          if (boxWrap.tagName !== 'LABEL' && boxWrap.dataset.amadaCheckboxBound !== 'true') {
            boxWrap.dataset.amadaCheckboxBound = 'true';
            boxWrap.addEventListener('click', function (event) {
              if (event.target.closest('input, label, a, button')) return;
              var input = event.currentTarget.querySelector('input[type="checkbox"]');
              if (input && !input.disabled) input.click();
            });
          }
        }
      }
    } else if (consentLabel && form.tagName === 'FORM' && !form.classList.contains('amada-hulk__native')) {
      var wrap = document.createElement('label');
      wrap.className = 'amada-hulk__consent';

      consent = document.createElement('input');
      consent.className = 'amada-hulk__check-control';
      consent.type = 'checkbox';
      consent.name = 'Amada consent';
      consent.value = 'Accepted';
      consent.required = true;

      var text = document.createElement('span');
      text.textContent = consentLabel;

      wrap.appendChild(consent);
      wrap.appendChild(text);

      var anchor = embed.querySelector('.amada-hulk__row');
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(wrap, anchor.nextSibling);
      else form.appendChild(wrap);
    }

    embed.dataset.amadaRestyled = 'true';
  }

  // Hulk validates server-side and reports afterwards. Gating the submit means a missing name is
  // caught in the field rather than as a red banner after a round trip — and it is the last
  // moment at which the shopper's current variant can still be written into the payload.
  function guard(root, embed) {
    var form = embed.querySelector('form');
    if (!form || form.dataset.amadaGuarded === 'true') return;
    form.dataset.amadaGuarded = 'true';

    var validate = function (event) {
      var submitted =
        event.type === 'submit' ||
        (event.target &&
          event.target.closest &&
          event.target.closest('[type="submit"], button:not([type])'));
      if (!submitted) return;

      if (root.dataset.amadaPending === 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      fill(root, embed);

      if (form.checkValidity()) {
        if (event.type === 'click') beginPending(root, embed);
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      form.reportValidity();
    };

    form.addEventListener('click', validate, true);
    form.addEventListener('submit', validate, true);
  }

  function isVisible(node) {
    return (
      node &&
      node.getClientRects().length > 0 &&
      window.getComputedStyle(node).display !== 'none' &&
      window.getComputedStyle(node).visibility !== 'hidden'
    );
  }

  function submitButton(embed) {
    return firstMatching(embed, [
      '[type="submit"]',
      'button.btn',
      'button[class*="submit"]',
      'button:not([type="button"]):not([type="reset"])',
    ]);
  }

  function statusText(root, name, fallback) {
    var value = attribute(root, 'data-amada-' + name, fallback);
    var normalized = normalize(value);
    if (!normalized || normalized === 'true' || normalized === 'false') return fallback;
    return value;
  }

  function showStatus(root, state, text) {
    var note = root.querySelector('.amada-hulk__note');
    if (!note) {
      note = document.createElement('p');
      note.className = 'amada-hulk__note';
      note.setAttribute('role', 'status');
      root.appendChild(note);
    }
    note.dataset.state = state;
    note.textContent = text;
    note.hidden = false;
  }

  function clearStatus(root) {
    var note = root.querySelector('.amada-hulk__note');
    if (!note) return;
    note.hidden = true;
    note.textContent = '';
    delete note.dataset.state;
  }

  function hideHulkModalNodes() {
    var modals = document.querySelectorAll('.swal-overlay, .sweet-overlay, .swal2-container');
    for (var i = 0; i < modals.length; i++) {
      modals[i].style.setProperty('display', 'none', 'important');
      modals[i].setAttribute('aria-hidden', 'true');
    }
  }

  function ensureSafeSwalClose() {
    if (
      !window.swal ||
      typeof window.swal.close !== 'function' ||
      window.swal.close.amadaSafe
    ) {
      return;
    }

    var originalClose = window.swal.close;
    var safeClose = function () {
      try {
        return originalClose.apply(window.swal, arguments);
      } catch (error) {
        hideHulkModalNodes();
        return undefined;
      }
    };
    safeClose.amadaSafe = true;
    window.swal.close = safeClose;
  }

  function closeHulkModal() {
    ensureSafeSwalClose();
    if (window.swal && typeof window.swal.close === 'function') window.swal.close();
    hideHulkModalNodes();
  }

  function finishPending(root, embed) {
    if (root.amadaSubmitTimer) {
      window.clearTimeout(root.amadaSubmitTimer);
      root.amadaSubmitTimer = null;
    }

    root.dataset.amadaPending = 'false';
    root.removeAttribute('aria-busy');
    document.body.classList.remove('amada-hulk-is-submitting');

    var submit = submitButton(embed);
    if (!submit) return;
    submit.disabled = false;
    submit.removeAttribute('aria-disabled');
    if (submit.dataset.amadaLabel) {
      if (submit.tagName === 'INPUT') submit.value = submit.dataset.amadaLabel;
      else submit.textContent = submit.dataset.amadaLabel;
    }
  }

  function fail(root, embed) {
    finishPending(root, embed);
    closeHulkModal();
    showStatus(
      root,
      'error',
      statusText(root, 'error', 'Something went wrong. Please try again.')
    );
  }

  function beginPending(root, embed) {
    if (root.dataset.amadaPending === 'true') return;
    root.dataset.amadaPending = 'true';
    root.setAttribute('aria-busy', 'true');
    document.body.classList.add('amada-hulk-is-submitting');
    clearStatus(root);
    ensureSafeSwalClose();

    var submit = submitButton(embed);
    if (submit) {
      var label = submit.tagName === 'INPUT' ? submit.value : submit.textContent;
      if (!submit.dataset.amadaLabel) submit.dataset.amadaLabel = label;
      var pendingLabel = statusText(root, 'sending', 'Sending…');
      if (submit.tagName === 'INPUT') submit.value = pendingLabel;
      else submit.textContent = pendingLabel;
      submit.setAttribute('aria-disabled', 'true');
      window.setTimeout(function () {
        if (root.dataset.amadaPending === 'true' && submit.isConnected) submit.disabled = true;
      }, 0);
    }

    // Hulk opens SweetAlert synchronously after its validation pass. Its network request still
    // runs; only the modal is removed so the stable, inline button state is all the shopper sees.
    window.setTimeout(closeHulkModal, 0);
    window.requestAnimationFrame(closeHulkModal);

    root.amadaSubmitTimer = window.setTimeout(function () {
      if (root.dataset.amadaPending === 'true') fail(root, embed);
    }, SUBMIT_TIMEOUT_MS);
  }

  function hasError(embed) {
    var errors = embed.querySelectorAll(
      '.alert-danger, .field-error, [class*="error-message" i], [class*="invalid-feedback" i]'
    );
    for (var i = 0; i < errors.length; i++) {
      if (normalize(errors[i].textContent)) return true;
    }
    return false;
  }

  function looksLikeSuccess(embed) {
    var submittedResponse = embed.querySelector('.after_form_submit .from_res');
    if (submittedResponse && !embed.querySelector('form')) return true;

    if (hasError(embed)) return false;

    var successNodes = embed.querySelectorAll(
      '.success, .thankyou, .thank-you, [class*="success" i], [class*="Success"], [class*="thank" i], [class*="Thank"]'
    );
    for (var i = 0; i < successNodes.length; i++) {
      if (isVisible(successNodes[i])) return true;
    }

    var hasFields = embed.querySelector(
      'input[type="email"], input[type="text"], textarea, [type="submit"]'
    );
    if (!hasFields && /thank|sent|success|received|submitted/.test(normalize(embed.textContent))) {
      return true;
    }

    return false;
  }

  function shopUuid() {
    try {
      var builder = window.hulkFormBuilder;
      if (!builder) return '';
      if (builder.shop && builder.shop.shop_uuid) return String(builder.shop.shop_uuid);
      if (builder.shop_id) return String(builder.shop_id);

      var data = builder.shop_data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (error) {
          data = null;
        }
      }
      if (data) {
        for (var key in data) {
          if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
          var shop = data[key];
          if (shop && shop.shop_uuid) {
            if (!builder.shop) builder.shop = shop;
            return String(shop.shop_uuid);
          }
        }
      }
    } catch (error) {
      /* Shop config is on the page; without it Hulk treats the post as an unknown shop. */
    }
    return '';
  }

  function responseLabel(name) {
    return RESPONSE_LABELS[name] || name;
  }

  function collectShopper(root, embed) {
    var name =
      (embed &&
        (findByLabel(embed, ['name', 'your name', 'full name']) ||
          firstMatching(embed, [
            'input[autocomplete="name"]',
            'input[autocomplete="given-name"]',
            'input[name*="name" i]:not([type="hidden"])',
            '.amada-hulk__input--name',
          ]))) ||
      root.querySelector('.amada-hulk__input--name, input[autocomplete="name"]');
    var email =
      (embed &&
        (findByLabel(embed, ['email', 'email address', 'e-mail']) ||
          firstMatching(embed, ['input[type="email"]', 'input[name*="email" i]']))) ||
      root.querySelector('input[type="email"]');

    return {
      name: name && name.value ? name.value.trim() : '',
      email: email && email.value ? email.value.trim() : '',
      nameNode: name,
      emailNode: email,
    };
  }

  function buildFormResponse(root, shopper) {
    var values = fields(root);
    var payload = {};
    payload[responseLabel('email')] = shopper.email || '';
    payload[responseLabel('name')] = shopper.name || '';

    for (var name in values) {
      if (!Object.prototype.hasOwnProperty.call(values, name)) continue;
      if (name === 'email' || name === 'name') continue;
      var value = values[name];
      if (value === undefined || value === null || value === '') continue;
      payload[responseLabel(name)] = String(value);
    }

    return payload;
  }

  function postHulkResponse(root, shopper) {
    var formId = root.getAttribute('data-amada-form');
    var uuid = shopUuid();
    if (!uuid) {
      return Promise.reject(new Error('Hulk shop UUID is missing'));
    }

    var headers = {
      'Content-Type': 'application/json',
      'X-Shop-UUID': uuid,
    };
    var body = JSON.stringify({
      form_uuid: formId,
      formResponse: JSON.stringify(buildFormResponse(root, shopper)),
      confirmationMail: true,
      email_check: shopper.email || '',
      utm_params: JSON.stringify(attribution()),
      referrer_url: window.location.href.split('#')[0],
    });

    return fetch(HULK_FORM_HOST + '/ajaxcall/formresponse', {
      method: 'POST',
      headers: headers,
      body: body,
    }).then(function (response) {
      var type = (response.headers.get('content-type') || '').toLowerCase();
      if (!response.ok || type.indexOf('application/json') === -1) {
        throw new Error('Hulk response ' + response.status);
      }
      return response.json();
    }).then(function (data) {
      if (data && typeof data === 'object') {
        var blob = JSON.stringify(data);
        if (data.charge_failed || /free plan|upgrade your plan|please upgrade/i.test(blob)) {
          throw new Error(data.message || data.error || 'Hulk plan error');
        }
      }
      return data || {};
    });
  }

  function succeed(root, embed) {
    if (root.dataset.amadaSucceeded === 'true') return;
    root.dataset.amadaSucceeded = 'true';
    finishPending(root, embed);

    if (embed) embed.setAttribute('hidden', '');
    var nativeForm = root.querySelector('form.amada-hulk__native');
    if (nativeForm) nativeForm.setAttribute('hidden', '');

    showStatus(
      root,
      'success',
      statusText(root, 'success', 'Sent — check your inbox.')
    );

    // Hulk announces itself with a SweetAlert modal over the page. The theme has already said
    // the same thing in place, quietly.
    closeHulkModal();

    root.dispatchEvent(
      new CustomEvent('amada:hulk:success', {
        bubbles: true,
        detail: { form: root.getAttribute('data-amada-form'), fields: fields(root) },
      })
    );
  }

  function watchSubmission(root, embed) {
    var observer = new MutationObserver(function () {
      if (looksLikeSuccess(embed)) {
        succeed(root, embed);
        observer.disconnect();
      } else if (root.dataset.amadaPending === 'true' && hasError(embed)) {
        fail(root, embed);
      }
    });
    observer.observe(embed, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    embed.addEventListener(
      'submit',
      function () {
        beginPending(root, embed);
        window.setTimeout(function () {
          if (looksLikeSuccess(embed)) succeed(root, embed);
        }, 400);
      },
      true
    );
  }

  function bindNativeForm(root, embed) {
    if (root.dataset.amadaNativeBound === 'true') return;
    root.dataset.amadaNativeBound = 'true';

    var form = root.querySelector('form.amada-hulk__native');
    if (!form) return;

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (root.dataset.amadaPending === 'true') return;

      var shopper = collectShopper(root, form);
      if (shopper.emailNode) shopper.emailNode.setCustomValidity('');
      if (shopper.nameNode) shopper.nameNode.setCustomValidity('');

      if (!shopper.email) {
        if (shopper.emailNode) {
          shopper.emailNode.setCustomValidity('Please enter your email.');
          shopper.emailNode.reportValidity();
        }
        return;
      }

      beginPending(root, form);
      postHulkResponse(root, shopper)
        .then(function () {
          succeed(root, embed || form);
        })
        .catch(function () {
          fail(root, form);
        });
    });
  }

  function mountNativeForm(root, embed) {
    if (root.querySelector('form.amada-hulk__native')) {
      bindNativeForm(root, embed);
      return true;
    }

    var namePlaceholder = root.getAttribute('data-amada-name-placeholder') || 'Your name';
    var emailPlaceholder = root.getAttribute('data-amada-email-placeholder') || 'Enter your email';
    var submitLabel = root.getAttribute('data-amada-submit-label') || 'SAVE MY PIECE';
    var formId = (embed && embed.id) || root.getAttribute('data-amada-form') || 'amada-hulk';

    var form = document.createElement('form');
    form.className = 'amada-hulk__form amada-hulk__native';
    form.setAttribute('novalidate', '');

    var nameWrap = document.createElement('div');
    nameWrap.className = 'amada-hulk__name';
    var nameLabel = document.createElement('label');
    nameLabel.setAttribute('for', formId + '-name');
    nameLabel.textContent = 'Name';
    var nameInput = document.createElement('input');
    nameInput.className = 'amada-hulk__input amada-hulk__input--name';
    nameInput.id = formId + '-name';
    nameInput.type = 'text';
    nameInput.name = 'Name';
    nameInput.autocomplete = 'name';
    nameInput.required = true;
    nameInput.setAttribute('aria-required', 'true');
    nameInput.placeholder = namePlaceholder;
    nameWrap.appendChild(nameLabel);
    nameWrap.appendChild(nameInput);

    var emailWrap = document.createElement('div');
    emailWrap.className = 'amada-hulk__email';
    var emailLabel = document.createElement('label');
    emailLabel.setAttribute('for', formId + '-email');
    emailLabel.textContent = 'Email';
    var emailInput = document.createElement('input');
    emailInput.className = 'amada-hulk__input';
    emailInput.id = formId + '-email';
    emailInput.type = 'email';
    emailInput.name = 'Email';
    emailInput.autocomplete = 'email';
    emailInput.required = true;
    emailInput.setAttribute('aria-required', 'true');
    emailInput.placeholder = emailPlaceholder;
    emailWrap.appendChild(emailLabel);
    emailWrap.appendChild(emailInput);

    var submitWrap = document.createElement('div');
    submitWrap.className = 'amada-hulk__submit-wrap';
    var submit = document.createElement('button');
    submit.className = 'amada-hulk__submit';
    submit.type = 'submit';
    submit.textContent = submitLabel;
    submitWrap.appendChild(submit);

    form.appendChild(nameWrap);
    form.appendChild(emailWrap);
    form.appendChild(submitWrap);
    root.appendChild(form);
    root.classList.add('amada-hulk--native');
    bindNativeForm(root, embed);
    return true;
  }

  function hideIframe(iframe) {
    iframe.setAttribute('hidden', '');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.setProperty('display', 'none', 'important');
    iframe.style.setProperty('height', '0', 'important');
    iframe.style.setProperty('min-height', '0', 'important');
    iframe.style.setProperty('pointer-events', 'none', 'important');
    var host = iframe.parentElement;
    if (host) {
      host.classList.remove('hulk-fb-is-loading');
      host.removeAttribute('aria-busy');
      var spinner = host.querySelector('.hulk-fb-spinner');
      if (spinner) spinner.remove();
    }
  }

  function applyIframe(root, embed, iframe) {
    if (embed.dataset.amadaApplied === 'true') return;
    embed.dataset.amadaApplied = 'true';

    hideIframe(iframe);
    mountNativeForm(root, embed);
    restyle(root, root);
    watchSubmission(root, embed);
    embed.classList.add('amada-hulk__embed--ready');
  }

  function report(root, embed, missing) {
    if (!missing.length || embed.dataset.amadaWarned === 'true') return;
    embed.dataset.amadaWarned = 'true';
    console.warn(
      '[Amada] Hulk form ' +
        root.getAttribute('data-amada-form') +
        ' is missing fields for: ' +
        missing.join(', ') +
        '. Add each as a Hidden field with that exact name, or the email templates will render ' +
        'those tokens empty. See ADMIN-TASKS.md → "Hulk Form Builder".'
    );
  }

  function rendered(embed) {
    return !!(
      embed.querySelector('form') ||
      embed.querySelector('input[type="email"]') ||
      embed.querySelector('iframe')
    );
  }

  function apply(root, embed) {
    if (embed.dataset.amadaApplied === 'true') return;

    var iframe = embed.querySelector('iframe');
    if (iframe && !embed.querySelector('form, input[type="email"]')) {
      applyIframe(root, embed, iframe);
      return;
    }

    embed.dataset.amadaApplied = 'true';
    report(root, embed, fill(root, embed));
    restyle(root, embed);
    guard(root, embed);
    watchSubmission(root, embed);
    embed.classList.add('amada-hulk__embed--ready');
  }

  function bind(root) {
    if (root.dataset.amadaBound === 'true') return;
    root.dataset.amadaBound = 'true';

    var id = root.getAttribute('data-amada-form');
    var embed = (id && root.querySelector('#' + CSS.escape(id))) || root.querySelector('.pxFormGenerator');
    if (!embed) {
      console.warn('[Amada] Hulk embed #' + id + ' is missing from the page.');
      return;
    }

    if (rendered(embed)) {
      apply(root, embed);
      return;
    }

    // Hulk injects the form asynchronously and gives no ready event. Watch and poll together:
    // the observer catches the usual case immediately, the poll is what eventually gives up.
    var observer = new MutationObserver(function () {
      if (!rendered(embed)) return;
      observer.disconnect();
      apply(root, embed);
    });
    observer.observe(embed, { childList: true, subtree: true });
    observer.observe(root, { childList: true, subtree: true });

    var ticks = 0;
    var poll = window.setInterval(function () {
      ticks += 1;
      if (rendered(embed)) {
        window.clearInterval(poll);
        observer.disconnect();
        apply(root, embed);
        return;
      }
      if (ticks >= POLL_MAX) {
        window.clearInterval(poll);
        observer.disconnect();
        mountNativeForm(root, embed);
        restyle(root, embed);
        watchSubmission(root, embed);
        embed.classList.add('amada-hulk__embed--ready');
        console.warn('[Amada] Hulk form ' + id + ' did not render within ' + (POLL_MS * POLL_MAX) / 1000 + 's.');
      }
    }, POLL_MS);
  }

  function boot() {
    document.querySelectorAll('.amada-hulk[data-amada-form]').forEach(bind);
  }

  function refresh(root) {
    if (!root) {
      boot();
      return;
    }
    delete root.dataset.amadaBound;
    delete root.dataset.amadaNativeBound;
    var embed = root.querySelector('.pxFormGenerator');
    if (embed) {
      delete embed.dataset.amadaApplied;
      delete embed.dataset.amadaNativeBound;
      delete embed.dataset.amadaRestyled;
      delete embed.dataset.amadaWarned;
    }
    bind(root);
  }

  window.AmadaHulk = { boot: boot, fields: fields, refresh: refresh };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Section rendering (variant morphs, editor edits) can bring a new panel in after boot.
  document.addEventListener('shopify:section:load', boot);
})();
